import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Divider } from '@factorialco/deprecated-design-system'
import { F0Button as Button, F0Text, ProgressBarChart } from '@factorialco/f0-react'
import { BookOpen, CheckDouble } from '@factorialco/f0-react/icons/app'
import { CreateBlockProgressDocument, LoadContentDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { FeatureWall } from 'modules/core/components/FeatureWall'
import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ContentConsumer from 'modules/trainings/components/content/Consumer'
import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { ContentConsumerCallbacks } from 'modules/trainings/components/content/types'
import { Block } from 'modules/trainings/lib/content/Block'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

export default observer(() => {
  const { trainingId } = useParams()
  const { employee: currentEmployee } = useCurrentSession()

  const { data, loading } = useQuery(LoadContentDocument, {
    variables: {
      trainingId: Number(trainingId),
      employeeId: currentEmployee.id,
    },
  })

  const collection = useMemo(() => {
    const modules = data?.trainings.contentModules
    if (loading || !modules) {
      return null
    }
    return graphqlContentMapper.fromGraphQL(modules, Number(trainingId))
  }, [trainingId, data, loading])

  const [createBlockProgress] = useMutation(CreateBlockProgressDocument, {
    extractorFn(mutationData) {
      return mutationData.trainingsMutations.createTrainingsContentBlockProgress
    },
  })

  const consumerRef = useRef<ContentConsumerCallbacks>(null)
  const [nextButtonLabel, setNextButtonLabel] = useState('')
  const [nextButtonEnabled, setNextButtonEnabled] = useState(true)
  const updateNextButton = () => {
    const nextLabel = consumerRef.current ? consumerRef.current.nextLabel() : ''
    setNextButtonLabel(nextLabel ? nextLabel : i18n.t('trainings.content.consumer.continue'))

    setNextButtonEnabled(consumerRef.current ? consumerRef.current.nextEnabled() : true)
  }
  useEffect(() => {
    updateNextButton()
  })

  if (!collection || loading) {
    return <Loading expand />
  }

  // Create an async function so the button shows a loading state
  const handleNext = async () => {
    if (collection.selectedItem instanceof Block) {
      const currentBlock = collection.selectedItem

      try {
        // Check block completed before creating the progress in the backend (for example video and quiz)
        if (consumerRef.current && !(await consumerRef.current?.handleNext())) {
          return
        }

        const result = await createBlockProgress({
          variables: {
            contentBlockId: currentBlock.id,
            employeeId: currentEmployee.id,
          },
        })

        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.error'),
          })
        })

        if (handled.success) {
          // If we enter here, the progress exists in backend, so we
          // mark the current block as completed and next block as accesible
          currentBlock.status = 'completed'
          currentBlock.accesible = true

          const items = collection.flatItems
          const currentIndex = items.findIndex((item) => item.uuid === currentBlock.uuid)
          if (currentIndex !== -1 && currentIndex < items.length - 1) {
            // Get the block after current
            let nextItem = items[currentIndex + 1]
            // if current block is last of module, next item will be the next module
            if (nextItem instanceof ContentModule && currentIndex < items.length - 2) {
              // get the next item, that will be the first block of the new module
              nextItem = items[currentIndex + 2]
            }
            if (nextItem instanceof Block) {
              // Set accesible next item on sidebar
              nextItem.accesible = true
            }

            // select next item
            nextItem = items[currentIndex + 1]
            if (nextItem) {
              collection.setSelected(nextItem.uuid)
            }
          }
        }
      } catch (_error) {
        // Mutation has failed
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error'),
        })
      }
    } else if (collection.selectedItem instanceof ContentModule) {
      collection.next()
    }
  }

  if (!collection.selectedItem) return null
  const nonModuleItems = collection.flatItems.filter((item) => !(item instanceof ContentModule))
  const completedItems = nonModuleItems.filter(
    (item) => item instanceof Block && item.status === 'completed'
  )

  const percentage = Math.round((completedItems.length / nonModuleItems.length) * 100)

  return (
    <FeatureWall featureName="dev_training_lms">
      <ResponsivePage.Body variant="fullscreen">
        <ContentConsumer
          tocItems={collection.modules.map((contentModule) => ({
            id: contentModule.uuid,
            label: contentModule.title,
            icon: BookOpen,
            onClick: () => collection.setSelected(contentModule.uuid),
            children: contentModule.blocks.map((block) => ({
              id: block.uuid,
              label: block.title,
              onClick: () => collection.setSelected(block.uuid),
              disabled: !block.accesible,
              icon:
                block.status === 'completed' ? CheckDouble : contentRegistry.fetch(block, 'icon'),
            })),
            // Allow empty modules to be clickable, only disable if has blocks but none are accessible
            disabled:
              contentModule.blocks.length > 0 &&
              !contentModule.blocks.some((block) => block.accesible),
          }))}
          selectedItem={collection.selectedItem}
          updateNextButton={updateNextButton}
          ref={consumerRef}
        />
        <Divider />
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          gap="s16"
          padding="s16"
          width="full"
          paddingRight="s64"
        >
          <Box width="s200" gap="s8" flexDirection="row" alignItems="center">
            <F0Text content={i18n.t('trainings.content.consumer.progress')} variant="description" />
            <Box width="full">
              <ProgressBarChart
                value={completedItems.length}
                max={nonModuleItems.length}
                label={`${percentage}%`} // Optional label shown on the right
              />
            </Box>
          </Box>
          <Box gap="s16" flexDirection="row">
            <Button
              label={i18n.t('trainings.content.consumer.back')}
              variant="outline"
              onClick={() => {
                collection.back()
              }}
            />
            <Button label={nextButtonLabel} disabled={!nextButtonEnabled} onClick={handleNext} />
            {/* Disable based on computed completion on the contentItem */}
          </Box>
        </Box>
      </ResponsivePage.Body>
    </FeatureWall>
  )
})
